import React from 'react';
import { motion } from 'framer-motion';
import { useFormContext } from '../../context/FormContext';
import { SectionWrapper } from './SectionWrapper';
import { TransactionsSection } from './sections/Transactions';
import { AnnualSpendSection } from './sections/AnnualSpend';
import { AssetsSection } from './sections/Assets';
import { LifecycleSection } from './sections/Lifecycle';
import { PurchaseMixSection } from './sections/PurchaseMix';
import { DistributionSection } from './sections/Distribution';
import { QualitativeSection } from './sections/Qualitative';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';

export const CompleteForm = () => {
  const { data, submitForm, setSubmitAttempted } = useFormContext();

  const missing = [];
  if (!data.contact.firstName) missing.push('First Name');
  if (!data.contact.lastName) missing.push('Last Name');
  if (!data.contact.email) missing.push('Email');

  const mixTotal = ['new', 'repeat', 'subscription', 'oneOff'].reduce((sum, key) => sum + (Number(data.form.purchaseMix[key]) || 0), 0);
  if (mixTotal !== 100) missing.push('Valid Purchase Mix (100%)');

  const distTotal = data.form.distribution.reduce((sum, d) => sum + (Number(d.percentage) || 0), 0);
  if (distTotal !== 100) missing.push('Valid Annual Distribution (100%)');

  const canSubmit = missing.length === 0;

  const handleFinalSubmit = () => {
    if (canSubmit) {
      submitForm();
    } else {
      setSubmitAttempted(true);
      // Scroll to first error
      const firstSection = document.getElementById('contact');
      if (firstSection) firstSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}>
      
      <div id="transactions">
        <SectionWrapper title="Transaction data" subtitle="Record details for each individual transaction line item." step="2">
          <TransactionsSection />
        </SectionWrapper>
      </div>

      <div id="spend">
        <SectionWrapper title="Annual spend by products and services" subtitle="Provide the total spend per year." step="3">
          <AnnualSpendSection />
        </SectionWrapper>
      </div>

      <div id="assets">
        <SectionWrapper title="Asset acquisition" subtitle="List assets acquired during the relevant period." step="4">
          <AssetsSection />
        </SectionWrapper>
      </div>

      <div id="lifecycle">
        <SectionWrapper title="Estimated asset lifecycle" step="5">
          <LifecycleSection />
        </SectionWrapper>
      </div>

      <div id="mix">
        <SectionWrapper 
          title="Estimated purchase mix" 
          subtitle="Percentages must total exactly 100%." 
          step="6"
          className={data.submitAttempted && mixTotal !== 100 ? 'ring-1 ring-red-400' : ''}
        >
          <PurchaseMixSection />
        </SectionWrapper>
      </div>

      <div id="distribution">
        <SectionWrapper 
          title="Estimated distribution by year" 
          subtitle="Percentages must total exactly 100%." 
          step="7"
          className={data.submitAttempted && distTotal !== 100 ? 'ring-1 ring-red-400' : ''}
        >
          <DistributionSection />
        </SectionWrapper>
      </div>

      <div id="qualitative">
        <SectionWrapper title="Qualitative context" subtitle="Provide detailed context for the quantitative data above." step="8">
          <QualitativeSection />
        </SectionWrapper>
      </div>

      <div id="submit-section">
        <Card className={!canSubmit && data.submitAttempted ? 'border-red-200 bg-red-50/10' : ''}>
          <div className="mb-6">
            <h3 className="text-[10px] font-bold text-brand-muted uppercase tracking-widest mb-2">Final Step</h3>
            <h2 className="text-xl font-serif text-brand-text">Submit the form</h2>
            <p className="text-sm text-brand-muted mt-1">Review your entries above. Once submitted, the payload is finalised.</p>
          </div>
          
          <Button 
            onClick={handleFinalSubmit} 
            className="w-full sm:w-auto px-10 h-12"
          >
            Submit Form
          </Button>
          
          {!canSubmit && data.submitAttempted && (
            <div className="mt-6 p-4 bg-red-50 border border-red-100 rounded-lg">
              <p className="text-xs font-bold text-red-600 uppercase tracking-widest mb-2">Submission Blocked</p>
              <ul className="space-y-1">
                {missing.map((item, i) => (
                  <li key={i} className="text-xs text-red-500 flex items-center gap-2">
                    <span className="w-1 h-1 bg-red-400 rounded-full"></span>
                    Please complete: {item}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </Card>
      </div>

    </motion.div>
  );
};